<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { throttle } from 'lodash-es';
    import * as pdfjs from 'pdfjs-dist';
    import { getPdfContext } from '../context/PdfContext';

    // Props
    export let pageNumber: number | undefined = undefined;
    export let selectable: boolean = true;

    // Context
    const { documentStore, navigationStore } = getPdfContext();

    // DOM Reference
    let textLayerRef: HTMLDivElement;
    let mounted = false;
    let mutationObserver: MutationObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let lastRenderKey = '';
    const ALIGN_THRESHOLD_PX = 0.25;

    // 현재 페이지 번호
    $: currentPageNum = pageNumber ?? $navigationStore.currentPage;

    // 마운트 상태 (반응형)
    $: isMounted = mounted && !!textLayerRef;

    // 헬퍼 함수: wrapper 찾기
    const getWrapper = (): HTMLElement | null => {
        return textLayerRef?.closest('.pdf-page-wrapper') as HTMLElement | null;
    };

    // PDF Canvas 렌더링 여부 확인
    const isPdfCanvasReady = (): boolean => {
        const wrapper = getWrapper();
        return !!wrapper?.querySelector('canvas[data-page]');
    };

    // Viewport 해상도 계산
    const resolveViewport = (page: pdfjs.PDFPageProxy, wrapper: HTMLElement) => {
        const scaleFactor = wrapper.dataset.scaleFactor || wrapper.style.getPropertyValue('--scale-factor') || '1';
        const scale = parseFloat(scaleFactor) || 1;
        return page.getViewport({ scale });
    };

    // Canvas와 텍스트 레이어 정렬
    const alignToCanvas = (wrapper: HTMLElement) => {
        if (!textLayerRef) return;

        const canvas = wrapper.querySelector('canvas[data-page]') as HTMLCanvasElement | null;
        if (!canvas) return;

        textLayerRef.style.transform = 'translate(0px, 0px)';

        const canvasRect = canvas.getBoundingClientRect();
        const layerRect = textLayerRef.getBoundingClientRect();
        const deltaX = layerRect.left - canvasRect.left;
        const deltaY = layerRect.top - canvasRect.top;

        if (Math.abs(deltaX) > ALIGN_THRESHOLD_PX || Math.abs(deltaY) > ALIGN_THRESHOLD_PX) {
            textLayerRef.style.transform = `translate(${-deltaX}px, ${-deltaY}px)`;
        }
    };

    // 텍스트 레이어 렌더링
    const renderTextLayer = async () => {
        if (!isMounted || !$documentStore.document) return;
        if (!isPdfCanvasReady()) return;

        const wrapper = getWrapper();
        if (!wrapper) return;

        try {
            const page = await documentStore.getPage(currentPageNum);
            if (!isMounted) return;

            const viewport = resolveViewport(page, wrapper);
            const renderKey = `${currentPageNum}:${Math.round(viewport.width)}x${Math.round(viewport.height)}`;

            if (renderKey === lastRenderKey) return;

            const textContent = await page.getTextContent();
            if (!isMounted) return;
            if (!textLayerRef) return;

            textLayerRef.innerHTML = '';

            const nativeWidth = parseFloat(wrapper.dataset.nativeWidth || `${viewport.width}`) || viewport.width;
            const nativeHeight = parseFloat(wrapper.dataset.nativeHeight || `${viewport.height}`) || viewport.height;
            textLayerRef.style.width = `${nativeWidth}px`;
            textLayerRef.style.height = `${nativeHeight}px`;

            await pdfjs.renderTextLayer({
                textContentSource: textContent,
                container: textLayerRef,
                viewport,
                textDivs: [],
            }).promise;

            if (!isMounted) return;

            alignToCanvas(wrapper);
            lastRenderKey = renderKey;
        } catch (error) {
            console.error('Error rendering text layer:', error);
        }
    };

    // 옵저버 정리
    const cleanupObservers = () => {
        mutationObserver?.disconnect();
        mutationObserver = null;
        resizeObserver?.disconnect();
        resizeObserver = null;
        throttledRender.cancel?.();
    };

    // ResizeObserver 설정
    const setupResizeObserver = () => {
        const wrapper = getWrapper();
        if (!wrapper) return;

        resizeObserver?.disconnect();
        resizeObserver = new ResizeObserver(() => {
            if (isMounted) {
                throttledRender();
            }
        });
        resizeObserver.observe(wrapper);
    };

    // Canvas 대기 및 렌더링 트리거
    const waitForCanvasAndRender = () => {
        if (!textLayerRef) return;

        const wrapper = getWrapper();
        if (!wrapper) return;

        mutationObserver?.disconnect();
        setupResizeObserver();

        if (isPdfCanvasReady()) {
            renderTextLayer();
            return;
        }

        mutationObserver = new MutationObserver(() => {
            if (isPdfCanvasReady()) {
                mutationObserver?.disconnect();
                mutationObserver = null;
                throttledRender();
            }
        });

        mutationObserver.observe(wrapper, {
            childList: true,
            subtree: true,
        });
    };

    // Throttled 렌더링
    const throttledRender = throttle(waitForCanvasAndRender, 32, { leading: true, trailing: true });

    // 마운트 시 초기화 (실제 렌더는 아래 reactive 블록이 단독으로 트리거 — 마운트당 2중 발화 방지)
    onMount(() => {
        mounted = true;
    });

    // 언마운트 시 정리
    onDestroy(() => {
        mounted = false;
        cleanupObservers();
    });

    // 페이지 번호 변경 시 재렌더링
    $: if (currentPageNum && mounted) {
        waitForCanvasAndRender();
    }
</script>

<div bind:this={textLayerRef} class="pdf-text-layer absolute left-0 top-0 h-full w-full" style:pointer-events={selectable ? 'auto' : 'none'}>
    <!-- 텍스트 레이어가 여기에 렌더링됩니다 -->
</div>

<style lang="postcss">
    :global(.pdf-text-layer) {
        z-index: 2;
        inset: 0;
        top: 0;
        left: 0;
        line-height: 1;
        transform-origin: 0 0;
        transform: translate3d(0, 0, 0);
    }

    :global(.pdf-text-layer span),
    :global(.pdf-text-layer br) {
        position: absolute;
        transform-origin: 0 0;
        white-space: pre;
        color: transparent;
        cursor: text;
        user-select: text;
        line-height: 1;
        transform: translate3d(0, 0, 0);
    }

    :global(.pdf-text-layer .highlight) {
        margin: -1px;
        padding: 1px;
    }

    /* br 요소 드래그 시 선택 강조가 보이지 않도록 처리 */
    :global(.pdf-text-layer br) {
        user-select: none;
    }
    :global(.pdf-text-layer br::selection) {
        background: transparent;
        color: transparent;
    }
</style>
